import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { FormEvent, useCallback, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation } from 'urql'
import { resetPasswordMutation } from '../../_lib/graphql/mutations'
import { FormField } from '../FormField'
import { PublicLayout } from '../PublicLayout'
import { useSnackbarPush } from '../SnackbarContext/SnackbarContext'

export const PasswordResetPage = () => {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') || ''
  const token = searchParams.get('token') || ''
  const { pushError } = useSnackbarPush()
  const [resetState, executeReset] = useMutation(resetPasswordMutation)
  const [password, setPassword] = useState('')
  const [passwordRepeat, setPasswordRepeat] = useState('')
  const [isReset, setIsReset] = useState(false)

  const handleSubmit = useCallback((event: FormEvent) => {
    event.preventDefault()

    if (password !== passwordRepeat) {
      pushError('Die Passwörter stimmen nicht überein.')
      return
    }

    executeReset({ input: { email, token, password } }).then(result => {
      if (result.error) {
        pushError(result.error.graphQLErrors[0]?.message || result.error.message)
        return
      }

      setIsReset(true)
    })
  }, [email, executeReset, password, passwordRepeat, pushError, token])

  return (
    <PublicLayout>
      <div className={'mx-auto mt-6 max-w-md'}>
        <div className={'rounded-3xl border border-sand-200 bg-white p-6 shadow-sm sm:p-8'}>
          {isReset ? (
            <div className={'text-center'}>
              <CheckCircleIcon className={'mx-auto h-14 w-14 text-primary'} />
              <h1 className={'mt-4 text-3xl font-bold tracking-tight text-primary-200'}>
                Passwort geändert
              </h1>
              <p className={'mt-3 text-base leading-7 text-gray-600'}>
                Du kannst dich jetzt mit deinem neuen Passwort anmelden.
              </p>
              <Link
                to={'/login'}
                className={'mt-6 inline-flex items-center justify-center rounded-full bg-primary-200 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
              >
                Zur Anmeldung
              </Link>
            </div>
          ) : !email || !token ? (
            <div className={'text-center'}>
              <h1 className={'text-3xl font-bold tracking-tight text-primary-200 text-balance'}>
                Link ungültig
              </h1>
              <p className={'mt-3 text-base leading-7 text-gray-600'}>
                Dieser Link ist unvollständig. Bitte fordere einen neuen an.
              </p>
              <Link
                to={'/passwort-vergessen'}
                className={'mt-6 inline-flex items-center justify-center rounded-full bg-primary-200 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
              >
                Neuen Link anfordern
              </Link>
            </div>
          ) : (
            <>
              <h1 className={'text-3xl font-bold tracking-tight text-primary-200 text-balance'}>
                Neues Passwort vergeben
              </h1>
              <p className={'mt-2 text-sm leading-6 text-gray-600'}>
                Für das Konto {email}
              </p>
              <form
                onSubmit={handleSubmit}
                className={'mt-6 space-y-5'}
              >
                <FormField
                  id={'password'}
                  label={'Neues Passwort'}
                  type={'password'}
                  autoComplete={'new-password'}
                  value={password}
                  onChange={setPassword}
                />
                <FormField
                  id={'passwordRepeat'}
                  label={'Passwort wiederholen'}
                  type={'password'}
                  autoComplete={'new-password'}
                  value={passwordRepeat}
                  onChange={setPasswordRepeat}
                />
                <button
                  type={'submit'}
                  disabled={resetState.fetching}
                  className={'flex w-full justify-center rounded-full bg-primary-200 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
                >
                  {resetState.fetching ? 'Wird gespeichert …' : 'Passwort speichern'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  )
}
